import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Layout from './pages/Layout';
import Inicio from './pages/Inicio';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CatalogosProvider } from './context/CatalogosContext';
import ToastContainer from './components/ui/ToastContainer';
import ConfirmHost from './components/ui/ConfirmHost';
import PromptHost from './components/ui/PromptHost';
import { ArrowPathIcon } from './components/ui/Icons';
import { lazyConReintento, CargandoModulo, LimiteErrorChunk } from './components/ui/CargaModulos';

// Pantallas fuera del menú (rutas públicas / kiosko) — carga diferida para que
// su código NO entre al bundle que bajan los colaboradores al arrancar.
const PaginaUbicacion = lazyConReintento(() => import('./pages/PaginaUbicacion'));
const TikTokOAuthCallback = lazyConReintento(() => import('./pages/TikTokOAuthCallback'));
const TikTokAccountsCallback = lazyConReintento(() => import('./pages/TikTokAccountsCallback'));

// Importar módulos de PEDIDOS (carga diferida)
const CrearPedido = lazyConReintento(() => import('./components/pedidos/CrearPedido'));
const VisualizarPedidos = lazyConReintento(() => import('./components/pedidos/VisualizarPedidos'));
const VisualizarPedidosOperativo = lazyConReintento(() => import('./components/pedidos/VisualizarPedidosOperativo'));
const VisualizarPedidosBackOffice = lazyConReintento(() => import('./components/pedidos/VisualizarPedidosBackOffice'));
const NotificarEnvioClientes = lazyConReintento(() => import('./components/pedidos/NotificarEnvioClientes'));
const PedidosAgencia = lazyConReintento(() => import('./components/pedidos/PedidosAgencia'));
const AnuladosSAC = lazyConReintento(() => import('./components/pedidos/AnuladosSAC'));
const SolicitarPedidoEspecial = lazyConReintento(() => import('./components/pedidos/SolicitarPedidoEspecial'));
const AutorizarSolicitudPE = lazyConReintento(() => import('./components/pedidos/AutorizarSolicitudPE'));
const PedidosCongelados = lazyConReintento(() => import('./components/pedidos/PedidosCongelados'));
const PedidosCongeladosOperativo = lazyConReintento(() => import('./components/pedidos/PedidosCongeladosOperativo'));
const ReportePedidos = lazyConReintento(() => import('./components/pedidos/ReportePedidos'));
const ReportePedidosOperativo = lazyConReintento(() => import('./components/pedidos/ReportePedidosOperativo'));
const SolicitudesAnulacion = lazyConReintento(() => import('./components/pedidos/SolicitudesAnulacion'));
const Devoluciones = lazyConReintento(() => import('./components/pedidos/Devoluciones'));
const VisualizarDevoluciones = lazyConReintento(() => import('./components/pedidos/VisualizarDevoluciones'));
const MovimientosPedido = lazyConReintento(() => import('./components/pedidos/MovimientosPedido'));
const VisualizarAutPagosPedidos = lazyConReintento(() => import('./components/pedidos/VisualizarAutPagosPedidos'));
const AutorizarPagosPedidos = lazyConReintento(() => import('./components/pedidos/AutorizarPagosPedidos'));
const CambiarEstadoPedido = lazyConReintento(() => import('./components/pedidos/CambiarEstadoPedido'));
const CorregirGuiasPedidos = lazyConReintento(() => import('./components/pedidos/CorregirGuiasPedidos'));
const CorreccionPedidos = lazyConReintento(() => import('./components/pedidos/CorreccionPedidos'));

// Importar módulos de SHOPIFY (carga diferida)
const PedidosShopify = lazyConReintento(() => import('./components/shopify/PedidosShopify'));
const PagosTarjeta = lazyConReintento(() => import('./components/shopify/PagosTarjeta'));
const FormularioCod = lazyConReintento(() => import('./components/shopify/FormularioCod'));
const GrabacionesTiendas = lazyConReintento(() => import('./components/shopify/GrabacionesTiendas'));
const Personalizador = lazyConReintento(() => import('./components/shopify/Personalizador'));
const AuditoriaPedidosShopify = lazyConReintento(() => import('./components/shopify/AuditoriaPedidosShopify'));
const AsignacionTiendasShopify = lazyConReintento(() => import('./components/shopify/AsignacionTiendasShopify'));
const SincronizacionInventarioShopify = lazyConReintento(() => import('./components/shopify/SincronizacionInventarioShopify'));
const AbandonadosBorradores = lazyConReintento(() => import('./components/shopify/AbandonadosBorradores'));
const MarketplaceTiktok = lazyConReintento(() => import('./components/shopify/MarketplaceTiktok'));

// Importar módulos de HERRAMIENTAS (carga diferida)
const MiPerfil = lazyConReintento(() => import('./components/perfil/MiPerfil'));
const AccesosRapidos = lazyConReintento(() => import('./components/herramientas/AccesosRapidos'));
const MisNotas = lazyConReintento(() => import('./components/herramientas/MisNotas'));
const AlertaDevoluciones = lazyConReintento(() => import('./components/herramientas/AlertaDevoluciones'));
const VerPedidosSimple = lazyConReintento(() => import('./components/herramientas/VerPedidosSimple'));
const VerMisPedidosSimple = lazyConReintento(() => import('./components/herramientas/VerMisPedidosSimple'));
const HistorialNotificaciones = lazyConReintento(() => import('./components/herramientas/HistorialNotificaciones'));
const EnviarNotificaciones = lazyConReintento(() => import('./components/herramientas/EnviarNotificaciones'));
const Solicitudes = lazyConReintento(() => import('./components/herramientas/Solicitudes'));
const Utilidades = lazyConReintento(() => import('./components/herramientas/Utilidades'));
const ManualUso = lazyConReintento(() => import('./components/manual/ManualUso'));
const Bitacora = lazyConReintento(() => import('./components/bitacora/Bitacora'));

// Importar módulos de BACKOFFICE (carga diferida)
const ColocarGuia = lazyConReintento(() => import('./components/pedidos/ColocarGuia'));
const AnuladosBackOffice = lazyConReintento(() => import('./components/backoffice/AnuladosBackOffice'));
const SolicitudesAnulacionForza = lazyConReintento(() => import('./components/backoffice/SolicitudesAnulacionForza'));
const ExcelParaForza = lazyConReintento(() => import('./components/pedidos/ExcelParaForza'));
const CrearGuiasForzaApi = lazyConReintento(() => import('./components/pedidos/CrearGuiasForzaApi'));
const GuiasManifiestos = lazyConReintento(() => import('./components/backoffice/GuiasManifiestos'));
const ExcelEntregasForza = lazyConReintento(() => import('./components/pedidos/ExcelEntregasForza'));
const LiquidacionesForza = lazyConReintento(() => import('./components/backoffice/LiquidacionesForza'));
const CierresPedidos = lazyConReintento(() => import('./components/backoffice/CierresPedidos'));

// Importar módulos de MENSAJERIA (carga diferida)
const VisualizarPedidosMensajeria = lazyConReintento(() => import('./components/mensajeria/VisualizarPedidosMensajeria'));
const NotificarClienteMensajeria = lazyConReintento(() => import('./components/mensajeria/NotificarClienteMensajeria'));
const RepartoPedidos = lazyConReintento(() => import('./components/mensajeria/RepartoPedidos'));
const TrasladoForza = lazyConReintento(() => import('./components/mensajeria/TrasladoForza'));
const ManifiestoPedidos = lazyConReintento(() => import('./components/mensajeria/ManifiestoPedidos'));
const GuiasCapital = lazyConReintento(() => import('./components/mensajeria/GuiasCapital'));
const AnuladosMensajeria = lazyConReintento(() => import('./components/mensajeria/AnuladosMensajeria'));
const SolicitudesAnulacionCapital = lazyConReintento(() => import('./components/mensajeria/SolicitudesAnulacionCapital'));
const CuentasPedidos = lazyConReintento(() => import('./components/mensajeria/CuentasPedidos'));
const AutorizarCuentasPedidos = lazyConReintento(() => import('./components/mensajeria/AutorizarCuentasPedidos'));
const ControlPedidosMensajero = lazyConReintento(() => import('./components/mensajeria/ControlPedidosMensajero'));


// Importar módulos de CREATIVO (carga diferida)
const CrearProyectos = lazyConReintento(() => import('./components/creativo/CrearProyectos'));
const IniciarProyectos = lazyConReintento(() => import('./components/creativo/IniciarProyectos'));
const KpisCreativo = lazyConReintento(() => import('./components/creativo/KpisCreativo'));
const ListadosKpisArtes = lazyConReintento(() => import('./components/creativo/ListadosKpisArtes'));
const RegistroKpisArtes = lazyConReintento(() => import('./components/creativo/RegistroKpisArtes'));
const KpisArtes = lazyConReintento(() => import('./components/creativo/KpisArtes'));

// Importar módulos de PUBLICIDAD (carga diferida)
const IngresosKpisSac = lazyConReintento(() => import('./components/publicidad/IngresosKpisSac'));
const MisKpisSac = lazyConReintento(() => import('./components/publicidad/MisKpisSac'));
const RegistrosKpisSac = lazyConReintento(() => import('./components/publicidad/RegistrosKpisSac'));
const AdsDashboard = lazyConReintento(() => import('./components/publicidad/AdsDashboard'));



// Importar módulos de PRODUCCIÓN (carga diferida)
const GestionRecetas = lazyConReintento(() => import('./components/produccion/GestionRecetas'));
const RegistrarMerma = lazyConReintento(() => import('./components/produccion/RegistrarMerma'));
const ControlImpresiones = lazyConReintento(() => import('./components/produccion/ControlImpresiones'));
const PedidosPersonalizados = lazyConReintento(() => import('./components/produccion/PedidosPersonalizados'));
const TiemposProduccion = lazyConReintento(() => import('./components/produccion/tiempos/TiemposProduccion'));
const EntregasProduccion = lazyConReintento(() => import('./components/produccion/entregas/EntregasProduccion'));

// Importar módulos de METAS (carga diferida)
const EstablecerMetasSAC = lazyConReintento(() => import('./components/metas/EstablecerMetasSAC'));
const ConsultarMetasSAC = lazyConReintento(() => import('./components/metas/ConsultarMetasSAC'));
const ConsultarMisMetas = lazyConReintento(() => import('./components/metas/ConsultarMisMetas'));

// Importar módulos de PRODUCTOS (carga diferida)
const GestionProductos = lazyConReintento(() => import('./components/productos/GestionProductos'));
const ProductosOperativo = lazyConReintento(() => import('./components/productos/ProductosOperativo'));
const ComplementosProductos = lazyConReintento(() => import('./components/productos/ComplementosProductos'));
const SetsProductos = lazyConReintento(() => import('./components/productos/SetsProductos'));

// Importar módulos de FINANCIERO (carga diferida)
const ControlFinancieroCapital = lazyConReintento(() => import('./components/financiero/ControlFinancieroCapital'));
const GastosPublicitarios = lazyConReintento(() => import('./components/financiero/GastosPublicitarios'));
const RegistrosGastosGeneral = lazyConReintento(() => import('./components/financiero/RegistrosGastosGeneral'));
const FacturacionForza = lazyConReintento(() => import('./components/financiero/FacturacionForza'));
const ConciliacionBancaria = lazyConReintento(() => import('./components/financiero/ConciliacionBancaria'));

// Importar módulos de KPIS (carga diferida)
const Kardex2 = lazyConReintento(() => import('./components/reportes/Kardex2'));
const MetasSACKPI = lazyConReintento(() => import('./components/kpis/MetasSACKPI'));
const MetasSACOperativo = lazyConReintento(() => import('./components/kpis/MetasSACOperativo'));
const IndicesCapital = lazyConReintento(() => import('./components/kpis/IndicesCapital'));
const ResultadosBrutos = lazyConReintento(() => import('./components/kpis/ResultadosBrutos'));
const AnalisisAnualMensual = lazyConReintento(() => import('./components/kpis/AnalisisAnualMensual'));
const ProyeccionDiarios = lazyConReintento(() => import('./components/kpis/ProyeccionDiarios'));

// Importar módulos de INVENTARIO (carga diferida)
const Kardex = lazyConReintento(() => import('./components/reportes/Kardex'));
const Kardex3 = lazyConReintento(() => import('./components/inventario/Kardex3'));
const AuditoriaInventario = lazyConReintento(() => import('./components/inventario/AuditoriaInventario'));
const AlertasReposicion = lazyConReintento(() => import('./components/inventario/AlertasReposicion'));
const Productos3D = lazyConReintento(() => import('./components/inventario/productos3d/Productos3D'));
const InventarioProductos =lazyConReintento(() => import('./components/reportes/InventarioProductos'));
const InventarioProductosDetallado = lazyConReintento(() => import('./components/reportes/InventarioProductosDetallado'));
const InventarioProductosOperativo = lazyConReintento(() => import('./components/reportes/InventarioProductosOperativo'));
const IngresoProductos = lazyConReintento(() => import('./components/inventario/IngresoProductos'));
const IngresoProductosOperativo = lazyConReintento(() => import('./components/inventario/IngresoProductosOperativo'));
const IngresoProductosDevoluciones = lazyConReintento(() => import('./components/inventario/IngresoProductosDevoluciones'));
const VisualizarIngresos = lazyConReintento(() => import('./components/inventario/VisualizarIngresos'));
const VisualizarIngresosBasico = lazyConReintento(() => import('./components/inventario/VisualizarIngresosBasico'));
const SeguimientoImportaciones = lazyConReintento(() => import('./components/inventario/SeguimientoImportaciones'));
const ReservasVsInventario = lazyConReintento(() => import('./components/inventario/ReservasVsInventario'));
const PrepararImportacion = lazyConReintento(() => import('./components/inventario/PrepararImportacion'));
const TrackingImportaciones = lazyConReintento(() => import('./components/importaciones/TrackingImportaciones'));
const CosteoImportaciones = lazyConReintento(() => import('./components/importaciones/CosteoImportaciones'));
const PedidosImportacion = lazyConReintento(() => import('./components/importaciones/PedidosImportacion'));
const ContarCajas = lazyConReintento(() => import('./components/importaciones/ContarCajas'));
const IdeasProductosNuevos = lazyConReintento(() => import('./components/importaciones/IdeasProductosNuevos'));
const ConsultarExistencias = lazyConReintento(() => import('./components/inventario/ConsultarExistencias'));
const TrasladosBodegas = lazyConReintento(() => import('./components/inventario/TrasladosBodegas'));
const VisualizarTraslados = lazyConReintento(() => import('./components/inventario/VisualizarTraslados'));
const GestionProveedores = lazyConReintento(() => import('./components/proveedores/GestionProveedores'));
const GestionBodegas = lazyConReintento(() => import('./components/bodegas/GestionBodegas'));

// Importar módulos de CLIENTES (carga diferida)
const GestionClientes = lazyConReintento(() => import('./components/clientes/GestionClientes'));
const ReportesClientes = lazyConReintento(() => import('./components/clientes/ReportesClientes'));
const GestionDepartamentos = lazyConReintento(() => import('./components/clientes/GestionDepartamentos'));

// Importar módulos de REPORTES (carga diferida)
const ReportePedidosUbicacion = lazyConReintento(() => import('./components/reportes/ReportePedidosUbicacion'));
const ReporteEnviadosForza = lazyConReintento(() => import('./components/reportes/ReporteEnviadosForza'));
const SeguimientoGeneralPedidos = lazyConReintento(() => import('./components/reportes/SeguimientoGeneralPedidos'));
const NotificarEnvioReporte = lazyConReintento(() => import('./components/reportes/NotificarEnvioReporte'));
const ReporteGeneralGanancias = lazyConReintento(() => import('./components/reportes/ReporteGeneralGanancias'));
const ReporteFinalGananciasNeto = lazyConReintento(() => import('./components/reportes/ReporteFinalGananciasNeto'));
const AuditarReporteGanancias = lazyConReintento(() => import('./components/reportes/AuditarReporteGanancias'));
const AnalisisDevoluciones = lazyConReintento(() => import('./components/reportes/AnalisisDevoluciones'));
const HistorialActividad = lazyConReintento(() => import('./components/reportes/HistorialActividad'));

// Importar módulos de ADMINISTRADOR (carga diferida)
const GestionUsuarios = lazyConReintento(() => import('./components/usuarios/GestionUsuarios'));
const GestionPermisos = lazyConReintento(() => import('./components/administrador/GestionPermisos'));
const GestionEmpresas = lazyConReintento(() => import('./components/empresas/GestionEmpresas'));
const Logos = lazyConReintento(() => import('./components/administrador/Logos'));
const TarifasCostos = lazyConReintento(() => import('./components/administrador/TarifasCostos'));
const ConfigurarDepartamentosEmpresa = lazyConReintento(() => import('./components/administrador/ConfigurarDepartamentosEmpresa'));
const IDClienteForza = lazyConReintento(() => import('./components/administrador/IDClienteForza'));
const LinkGuias = lazyConReintento(() => import('./components/administrador/LinkGuias'));
const ConfigurarNotificaciones = lazyConReintento(() => import('./components/administrador/ConfigurarNotificaciones'));
const ConfigurarTareas = lazyConReintento(() => import('./components/administrador/ConfigurarTareas'));
const Tareas = lazyConReintento(() => import('./components/tareas/Tareas'));

// Importar módulos de RRHH (carga diferida)
const Expedientes = lazyConReintento(() => import('./components/rrhh/Expedientes'));
const Vacaciones = lazyConReintento(() => import('./components/rrhh/Vacaciones'));
const Asistencia = lazyConReintento(() => import('./components/asistencia/Asistencia'));
const KioskoAsistencia = lazyConReintento(() => import('./components/asistencia/KioskoAsistencia'));
const MiAsistencia = lazyConReintento(() => import('./components/asistencia/MiAsistencia'));
// Cursos y certificados del personal (oct 2026, docs/planes/CURSOS_PLAN.md): Mis Cursos (todos) y Cursos (RRHH)
const MisCursos = lazyConReintento(() => import('./components/cursos/MisCursos'));
const Cursos = lazyConReintento(() => import('./components/cursos/Cursos'));
const EvaluacionDesempeno = lazyConReintento(() => import('./components/rrhh/EvaluacionDesempeno'));
// Mi Desempeño (23/10/2026, Evaluación del Desempeño tanda 6): cada colaborador ve su scorecard. Visible para todos.
const MiDesempeno = lazyConReintento(() => import('./components/rrhh/MiDesempeno'));
const Reconocimientos = lazyConReintento(() => import('./components/rrhh/Reconocimientos'));

// Importar módulos de IA / CHATBOT (carga diferida)
const RevisarPedidosIA = lazyConReintento(() => import('./components/chatbot/RevisarPedidosIA'));
const DemandaAgotados = lazyConReintento(() => import('./components/chatbot/DemandaAgotados'));
const AsignacionRedes = lazyConReintento(() => import('./components/chatbot/AsignacionRedes'));
const ConfiguracionPorEmpresa = lazyConReintento(() => import('./components/chatbot/ConfiguracionPorEmpresa'));
const CostosIA = lazyConReintento(() => import('./components/chatbot/CostosIA'));
const Conversaciones = lazyConReintento(() => import('./components/chatbot/Conversaciones'));
const BlacklistChatbot = lazyConReintento(() => import('./components/chatbot/BlacklistChatbot'));
const DescuentosIA = lazyConReintento(() => import('./components/chatbot/DescuentosIA'));
const LumiAdmin = lazyConReintento(() => import('./components/asistente/LumiAdmin'));
const CrearPedidoDesdeIA = lazyConReintento(() => import('./components/chatbot/CrearPedidoDesdeIA'));

// (La página de inicio se importa de forma estática arriba — es la home, carga al instante.)

// Widgets comentados - disponibles para uso futuro
// import MetasWidget from './components/widgets/MetasWidget';
// import EstadisticasPedidosWidget from './components/widgets/EstadisticasPedidosWidget';
// import EstadisticasDiaWidget from './components/widgets/EstadisticasDiaWidget';

// Componente de ruta protegida
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        height: '100vh',
        color: '#764ba2'
      }}>
        <span style={{ display: 'inline-flex', animation: 'spin 1s linear infinite' }}>
          <ArrowPathIcon className="w-8 h-8" />
        </span>
        <span style={{ color: '#666', fontSize: '14px' }}>Cargando...</span>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  // Primer ingreso pendiente (programa de Bienvenida, CURSOS_PLAN.md §0.1 #46): quien entró con la contraseña de su carta
  // y no creó la suya no ve el sistema. Vuelve al Login, que cierra esa sesión y le pide entrar y crearla.
  if (user.debeCambiarPassword === true) return <Navigate to="/login" replace state={{ primerIngresoPendiente: true }} />;
  return children;
}

// Email de la cuenta del kiosko de asistencia (pantalla dedicada, sin menú).
const KIOSKO_EMAIL = 'asistencia@paralellumsa.com';

// Si el usuario logueado es la cuenta del kiosko, lo mandamos directo al
// kiosko (nunca ve el menú/sistema). Cualquier otro usuario ve el Layout normal.
function LayoutOKiosko() {
  const { user } = useAuth();
  if (user && (user.email || '').toLowerCase() === KIOSKO_EMAIL) {
    return <Navigate to="/asistencia/kiosko" replace />;
  }
  return <Layout />;
}

function App() {
  // GLOBAL: la rueda del mouse NO debe cambiar el valor de los inputs numéricos.
  // Sobre un <input type="number"> enfocado, la rueda incrementa/decrementa el número
  // sin querer (errores graves en precios/cantidades). Acá, al hacer scroll sobre un
  // number enfocado, se evita el cambio y se desenfoca el campo para que el scroll siga
  // moviendo la página/modal. Cubre TODO el sistema (módulos viejos y nuevos) sin tocar
  // cada uno. Ver ESTETICA_GUIA.md §0.20.
  React.useEffect(() => {
    // Listener PASIVO a propósito: un wheel no-pasivo a nivel de documento frena el
    // scroll de todo el sistema (el navegador debe esperar al JS antes de mover la
    // página). Al desenfocar el input numérico, la rueda deja de cambiar el número y
    // el scroll sigue fluido — sin necesidad de preventDefault.
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number' && el === e.target) {
        el.blur(); // desenfoca → la rueda no cambia el número y el scroll sigue normal
      }
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <AuthProvider>
      <CatalogosProvider>
      <Router>
        <LimiteErrorChunk>
        <Suspense fallback={<CargandoModulo />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Ruta PÚBLICA — captura de ubicación del cliente (sin login) */}
          <Route path="/u/:token" element={<PaginaUbicacion />} />

          {/* Ruta PÚBLICA — callback OAuth de TikTok Ads (sin login, TikTok redirige aquí) */}
          <Route path="/tiktok-oauth-callback" element={<TikTokOAuthCallback />} />
          {/* OAuth de CUENTAS TikTok por marca (Puerta 2, TIKTOK_IA.md Fase 2). La URL
              está registrada en el portal de TikTok — si cambia acá, cambia allá. */}
          <Route path="/tiktok-accounts-callback" element={<TikTokAccountsCallback />} />

          {/* Kiosko de Asistencia — pantalla completa, FUERA del Layout (sin menú).
              Requiere login con la cuenta de kiosko (permisos mínimos). */}
          <Route path="/asistencia/kiosko" element={
            <ProtectedRoute>
              <KioskoAsistencia />
            </ProtectedRoute>
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <LayoutOKiosko />
            </ProtectedRoute>
          }>
            <Route index element={<Inicio />} />
            
            {/* Rutas de PEDIDOS */}
<Route path="crear-pedido" element={<div className="module-content"><CrearPedido /></div>} />
<Route path="visualizar-pedidos" element={<div className="module-content"><VisualizarPedidos /></div>} />
<Route path="visualizar-pedidos-operativo" element={<div className="module-content"><VisualizarPedidosOperativo /></div>} />
<Route path="visualizar-pedidos-backoffice" element={<div className="module-content"><VisualizarPedidosBackOffice /></div>} />
<Route path="notificar-envio-clientes" element={<div className="module-content"><NotificarEnvioClientes /></div>} />
<Route path="pedidos-agencia" element={<div className="module-content"><PedidosAgencia /></div>} />
<Route path="anulados-sac" element={<div className="module-content"><AnuladosSAC /></div>} />
<Route path="solicitudes-anulacion" element={<div className="module-content"><SolicitudesAnulacion /></div>} />
<Route path="solicitar-pedido-especial" element={<div className="module-content"><SolicitarPedidoEspecial /></div>} />
<Route path="autorizar-solicitud-pe" element={<div className="module-content"><AutorizarSolicitudPE /></div>} />
<Route path="pedidos-congelados" element={<div className="module-content"><PedidosCongelados /></div>} />
<Route path="pedidos-congelados-operativo" element={<div className="module-content"><PedidosCongeladosOperativo /></div>} />
<Route path="reporte-pedidos" element={<div className="module-content"><ReportePedidos /></div>} />
<Route path="reporte-pedidos-operativo" element={<div className="module-content"><ReportePedidosOperativo /></div>} />
<Route path="devoluciones" element={<div className="module-content"><Devoluciones /></div>} />
<Route path="visualizar-devoluciones" element={<div className="module-content"><VisualizarDevoluciones /></div>} />
<Route path="movimientos-pedido" element={<div className="module-content"><MovimientosPedido /></div>} />
<Route path="visualizar-aut-pagos-pedidos" element={<div className="module-content"><VisualizarAutPagosPedidos /></div>} />
<Route path="autorizar-pagos-pedidos" element={<div className="module-content"><AutorizarPagosPedidos /></div>} />
<Route path="cambiar-estado-pedido" element={<div className="module-content"><CambiarEstadoPedido /></div>} />
<Route path="corregir-guias-pedidos" element={<div className="module-content"><CorregirGuiasPedidos /></div>} />
<Route path="correccion-pedidos" element={<div className="module-content"><CorreccionPedidos /></div>} />

{/* Rutas de SHOPIFY */}
<Route path="pedidos-shopify" element={<div className="module-content"><PedidosShopify /></div>} />
<Route path="pagos-tarjeta" element={<div className="module-content"><PagosTarjeta /></div>} />
<Route path="auditoria-pedidos-shopify" element={<div className="module-content"><AuditoriaPedidosShopify /></div>} />
<Route path="abandonados-borradores" element={<div className="module-content"><AbandonadosBorradores /></div>} />
<Route path="marketplace-tiktok" element={<div className="module-content"><MarketplaceTiktok /></div>} />
<Route path="asignacion-tiendas-shopify" element={<div className="module-content"><AsignacionTiendasShopify /></div>} />
<Route path="sincronizacion-inventario-shopify" element={<div className="module-content"><SincronizacionInventarioShopify /></div>} />

{/* Rutas de HERRAMIENTAS */}
<Route path="mi-perfil" element={<div className="module-content"><MiPerfil /></div>} />
<Route path="accesos-rapidos" element={<div className="module-content"><AccesosRapidos /></div>} />
<Route path="tareas" element={<div className="module-content"><Tareas /></div>} />
<Route path="mis-notas" element={<div className="module-content"><MisNotas /></div>} />
<Route path="alerta-devoluciones" element={<div className="module-content"><AlertaDevoluciones /></div>} />
<Route path="ver-pedidos-simple" element={<div className="module-content"><VerPedidosSimple /></div>} />
<Route path="ver-mis-pedidos-simple" element={<div className="module-content"><VerMisPedidosSimple /></div>} />
<Route path="historial-notificaciones" element={<div className="module-content"><HistorialNotificaciones /></div>} />
<Route path="enviar-notificaciones" element={<div className="module-content"><EnviarNotificaciones /></div>} />
<Route path="solicitudes" element={<div className="module-content"><Solicitudes /></div>} />
<Route path="utilidades" element={<div className="module-content"><Utilidades /></div>} />
<Route path="manual-uso" element={<div className="module-content"><ManualUso /></div>} />
<Route path="bitacora" element={<div className="module-content"><Bitacora /></div>} />

{/* Rutas de BACKOFFICE */}
<Route path="colocar-guia" element={<div className="module-content"><ColocarGuia /></div>} />
<Route path="anulados-backoffice" element={<div className="module-content"><AnuladosBackOffice /></div>} />
<Route path="solicitudes-anulacion-forza" element={<div className="module-content"><SolicitudesAnulacionForza /></div>} />
<Route path="excel-para-forza" element={<div className="module-content"><ExcelParaForza /></div>} />
<Route path="crear-guias-forza-api" element={<div className="module-content"><CrearGuiasForzaApi /></div>} />
<Route path="guias-manifiestos" element={<div className="module-content"><GuiasManifiestos /></div>} />
<Route path="excel-entregas-forza" element={<div className="module-content"><ExcelEntregasForza /></div>} />
<Route path="liquidaciones-forza" element={<div className="module-content"><LiquidacionesForza /></div>} />
<Route path="cierres-pedidos" element={<div className="module-content"><CierresPedidos /></div>} />

{/* Rutas de MENSAJERIA */}
<Route path="visualizar-pedidos-mensajeria" element={<div className="module-content"><VisualizarPedidosMensajeria /></div>} />
<Route path="notificar-cliente-mensajeria" element={<div className="module-content"><NotificarClienteMensajeria /></div>} />
<Route path="reparto-pedidos" element={<div className="module-content"><RepartoPedidos /></div>} />
<Route path="traslado-forza" element={<div className="module-content"><TrasladoForza /></div>} />
<Route path="manifiesto-pedidos" element={<div className="module-content"><ManifiestoPedidos /></div>} />
<Route path="guias-capital" element={<div className="module-content"><GuiasCapital /></div>} />
<Route path="anulados-mensajeria" element={<div className="module-content"><AnuladosMensajeria /></div>} />
<Route path="solicitudes-anulacion-capital" element={<div className="module-content"><SolicitudesAnulacionCapital /></div>} />
<Route path="cuentas-pedidos" element={<div className="module-content"><CuentasPedidos /></div>} />
<Route path="autorizar-cuentas-pedidos" element={<div className="module-content"><AutorizarCuentasPedidos /></div>} />
<Route path="control-pedidos-mensajero" element={<div className="module-content"><ControlPedidosMensajero /></div>} />


{/* Rutas de CREATIVO */}
<Route path="crear-proyectos" element={<div className="module-content"><CrearProyectos /></div>} />
<Route path="iniciar-proyectos" element={<div className="module-content"><IniciarProyectos /></div>} />
<Route path="kpis-creativo" element={<div className="module-content"><KpisCreativo /></div>} />
<Route path="listados-kpis-artes" element={<div className="module-content"><ListadosKpisArtes /></div>} />
<Route path="registro-kpis-artes" element={<div className="module-content"><RegistroKpisArtes /></div>} />
<Route path="kpis-artes" element={<div className="module-content"><KpisArtes /></div>} />

{/* Rutas de PUBLICIDAD */}
<Route path="ingresos-kpis-sac" element={<div className="module-content"><IngresosKpisSac /></div>} />
<Route path="mis-kpis-sac" element={<div className="module-content"><MisKpisSac /></div>} />
<Route path="registros-kpis-sac" element={<div className="module-content"><RegistrosKpisSac /></div>} />
<Route path="ads-dashboard" element={<div className="module-content"><AdsDashboard /></div>} />


{/* Rutas de PRODUCCIÓN */}
<Route path="gestion-recetas" element={<div className="module-content"><GestionRecetas /></div>} />
<Route path="registrar-merma" element={<div className="module-content"><RegistrarMerma /></div>} />
<Route path="control-impresiones" element={<div className="module-content"><ControlImpresiones /></div>} />
<Route path="pedidos-personalizados" element={<div className="module-content"><PedidosPersonalizados /></div>} />
<Route path="tiempos-produccion" element={<div className="module-content"><TiemposProduccion /></div>} />
<Route path="entregas-produccion" element={<div className="module-content"><EntregasProduccion /></div>} />

{/* Rutas de METAS */}
<Route path="establecer-metas-sac" element={<div className="module-content"><EstablecerMetasSAC /></div>} />
<Route path="consultar-metas-sac" element={<div className="module-content"><ConsultarMetasSAC /></div>} />
<Route path="consultar-mis-metas" element={<div className="module-content"><ConsultarMisMetas /></div>} />

{/* Rutas de PRODUCTOS */}
<Route path="productos" element={<div className="module-content"><GestionProductos /></div>} />
<Route path="productos-operativo" element={<div className="module-content"><ProductosOperativo /></div>} />
<Route path="complementos-productos" element={<div className="module-content"><ComplementosProductos /></div>} />
<Route path="sets-productos" element={<div className="module-content"><SetsProductos /></div>} />

{/* Rutas de FINANCIERO */}
<Route path="control-financiero-capital" element={<div className="module-content"><ControlFinancieroCapital /></div>} />
<Route path="gastos-publicitarios" element={<div className="module-content"><GastosPublicitarios /></div>} />
<Route path="registros-gastos" element={<div className="module-content"><RegistrosGastosGeneral /></div>} />
<Route path="facturacion-forza" element={<div className="module-content"><FacturacionForza /></div>} />
<Route path="conciliacion-bancaria" element={<div className="module-content"><ConciliacionBancaria /></div>} />

{/* Rutas de KPIS */}
<Route path="kardex2" element={<div className="module-content"><Kardex2 /></div>} />
<Route path="metas-sac-kpi" element={<div className="module-content"><MetasSACKPI /></div>} />
<Route path="metas-sac-operativo" element={<div className="module-content"><MetasSACOperativo /></div>} />
<Route path="indices-capital" element={<div className="module-content"><IndicesCapital /></div>} />
<Route path="resultados-brutos" element={<div className="module-content"><ResultadosBrutos /></div>} />
<Route path="analisis-anual-mensual" element={<div className="module-content"><AnalisisAnualMensual /></div>} />
<Route path="proyeccion-diarios" element={<div className="module-content"><ProyeccionDiarios /></div>} />

{/* Rutas de INVENTARIO */}
<Route path="consulta-existencias" element={<div className="module-content"><ConsultarExistencias /></div>} />
<Route path="kardex" element={<div className="module-content"><Kardex /></div>} />
<Route path="kardex3" element={<div className="module-content"><Kardex3 /></div>} />
<Route path="auditoria-inventario" element={<div className="module-content"><AuditoriaInventario /></div>} />
<Route path="alertas-reposicion" element={<div className="module-content"><AlertasReposicion /></div>} />
<Route path="productos-3d" element={<div className="module-content"><Productos3D /></div>} />
<Route path="inventario-productos" element={<div className="module-content"><InventarioProductos /></div>} />
<Route path="inventario-productos-detallado" element={<div className="module-content"><InventarioProductosDetallado /></div>} />
<Route path="inventario-productos-operativo" element={<div className="module-content"><InventarioProductosOperativo /></div>} />
<Route path="ingreso-productos" element={<div className="module-content"><IngresoProductos /></div>} />
<Route path="ingreso-productos-operativo" element={<div className="module-content"><IngresoProductosOperativo /></div>} />
<Route path="ingreso-productos-devoluciones" element={<div className="module-content"><IngresoProductosDevoluciones /></div>} />
<Route path="visualizar-ingresos" element={<div className="module-content"><VisualizarIngresos /></div>} />
<Route path="visualizar-ingresos-basico" element={<div className="module-content"><VisualizarIngresosBasico /></div>} />
<Route path="seguimiento-importaciones" element={<div className="module-content"><SeguimientoImportaciones /></div>} />
<Route path="reservas-vs-inventario" element={<div className="module-content"><ReservasVsInventario /></div>} />
<Route path="formulario-cod" element={<div className="module-content"><FormularioCod /></div>} />
<Route path="grabaciones-tiendas" element={<div className="module-content"><GrabacionesTiendas /></div>} />
<Route path="personalizador" element={<div className="module-content"><Personalizador /></div>} />
<Route path="preparar-importacion" element={<div className="module-content"><PrepararImportacion /></div>} />
<Route path="tracking-importaciones" element={<div className="module-content"><TrackingImportaciones /></div>} />
<Route path="costeo-importaciones" element={<div className="module-content"><CosteoImportaciones /></div>} />
<Route path="pedidos-importacion" element={<div className="module-content"><PedidosImportacion /></div>} />
<Route path="conteo-importacion" element={<div className="module-content"><ContarCajas /></div>} />
<Route path="ideas-productos-nuevos" element={<div className="module-content"><IdeasProductosNuevos /></div>} />
<Route path="traslados" element={<div className="module-content"><TrasladosBodegas /></div>} />
<Route path="visualizar-traslados" element={<div className="module-content"><VisualizarTraslados /></div>} />
<Route path="proveedores" element={<div className="module-content"><GestionProveedores /></div>} />
<Route path="bodegas" element={<div className="module-content"><GestionBodegas /></div>} />

{/* Rutas de CLIENTES */}
<Route path="clientes" element={<div className="module-content"><GestionClientes /></div>} />
<Route path="reportes-clientes" element={<div className="module-content"><ReportesClientes /></div>} />
<Route path="departamentos" element={<div className="module-content"><GestionDepartamentos /></div>} />

{/* Rutas de REPORTES */}
<Route path="reporte-ubicaciones" element={<div className="module-content"><ReportePedidosUbicacion /></div>} />
<Route path="reporte-enviados-forza" element={<div className="module-content"><ReporteEnviadosForza /></div>} />
<Route path="seguimiento-general-pedidos" element={<div className="module-content"><SeguimientoGeneralPedidos /></div>} />
<Route path="notificar-envio-reporte" element={<div className="module-content"><NotificarEnvioReporte /></div>} />
<Route path="reporte-ganancias" element={<div className="module-content"><ReporteGeneralGanancias /></div>} />
<Route path="reporte-final-neto" element={<div className="module-content"><ReporteFinalGananciasNeto /></div>} />
<Route path="auditoria-ganancias" element={<div className="module-content"><AuditarReporteGanancias /></div>} />
<Route path="analisis-devoluciones" element={<div className="module-content"><AnalisisDevoluciones /></div>} />
<Route path="historial-actividad" element={<div className="module-content"><HistorialActividad /></div>} />

{/* Rutas de RRHH */}
<Route path="expedientes" element={<div className="module-content"><Expedientes /></div>} />
<Route path="vacaciones" element={<div className="module-content"><Vacaciones /></div>} />
<Route path="evaluacion-desempeno" element={<div className="module-content"><EvaluacionDesempeno /></div>} />
<Route path="reconocimientos" element={<div className="module-content"><Reconocimientos /></div>} />
<Route path="asistencia" element={<div className="module-content"><Asistencia /></div>} />
<Route path="mi-asistencia" element={<div className="module-content"><MiAsistencia /></div>} />
<Route path="mi-desempeno" element={<div className="module-content"><MiDesempeno /></div>} />
<Route path="mis-cursos" element={<div className="module-content"><MisCursos /></div>} />
<Route path="cursos" element={<div className="module-content"><Cursos /></div>} />

{/* Rutas de IA / CHATBOT */}
<Route path="revisar-pedidos-ia" element={<div className="module-content"><RevisarPedidosIA /></div>} />
              <Route path="demanda-agotados" element={<div className="module-content"><DemandaAgotados /></div>} />
<Route path="asignacion-redes-ia" element={<div className="module-content"><AsignacionRedes /></div>} />
<Route path="configuracion-por-empresa-ia" element={<div className="module-content"><ConfiguracionPorEmpresa /></div>} />
<Route path="costos-ia" element={<div className="module-content"><CostosIA /></div>} />
<Route path="conversaciones-ia" element={<div className="module-content"><Conversaciones /></div>} />
<Route path="blacklist-chatbot" element={<div className="module-content"><BlacklistChatbot /></div>} />
<Route path="descuentos-ia" element={<div className="module-content"><DescuentosIA /></div>} />
<Route path="lumi" element={<div className="module-content"><LumiAdmin /></div>} />
<Route path="crear-pedido-desde-ia" element={<div className="module-content"><CrearPedidoDesdeIA /></div>} />

{/* Rutas de ADMINISTRADOR */}
<Route path="usuarios" element={<div className="module-content"><GestionUsuarios /></div>} />
<Route path="permisos" element={<div className="module-content"><GestionPermisos /></div>} />
<Route path="empresas" element={<div className="module-content"><GestionEmpresas /></div>} />
<Route path="logos" element={<div className="module-content"><Logos /></div>} />
<Route path="tarifas-costos" element={<div className="module-content"><TarifasCostos /></div>} />
<Route path="configurar-departamentos-empresa" element={<div className="module-content"><ConfigurarDepartamentosEmpresa /></div>} />
<Route path="id-cliente-forza" element={<div className="module-content"><IDClienteForza /></div>} />
<Route path="link-guias" element={<div className="module-content"><LinkGuias /></div>} />
<Route path="configurar-notificaciones" element={<div className="module-content"><ConfigurarNotificaciones /></div>} />
<Route path="configurar-tareas" element={<div className="module-content"><ConfigurarTareas /></div>} />
</Route>
        </Routes>
        </Suspense>
        </LimiteErrorChunk>
      </Router>
      {/* Avisos globales (toasts) — reemplazan alert(). Montado 1 vez, cubre todo. */}
      <ToastContainer />
      {/* Modal de confirmación global — reemplaza window.confirm(). */}
      <ConfirmHost />
      {/* Modal de entrada de texto global — reemplaza window.prompt(). */}
      <PromptHost />
      </CatalogosProvider>
    </AuthProvider>
  );
}

export default App;